import { isAfter } from 'date-fns';

import type { ApiCartTimeslot } from '../../../common/api/timeslots/timeslotModels';

import { Helpers } from '../../../../utility/helpers';

export const getInitialDay = (currentTimeslot: ApiCartTimeslot | null) => {
    return currentTimeslot && currentTimeslot.startTime
        ? new Date(currentTimeslot.startTime)
        : undefined;
};

export const getFormattedDate = (date: Date) => {
    const shortDate = Helpers.formatDateDdMmm(date, true).toLowerCase();

    if (Helpers.dateIsToday(date)) {
        return `Idag, ${shortDate}`;
    }

    if (Helpers.dateIsTomorrow(date)) {
        return `I morgon, ${shortDate}`;
    }

    const weekday = Helpers.getDayName(date.getDay());
    const weekDayFormatted = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    return `${weekDayFormatted}, ${shortDate}`;
};

export const formatHour = (date: number | Date) => {
    const newDate = new Date(date);

    const formatter = new Intl.DateTimeFormat('sv-SE', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        timeZone: 'Europe/Stockholm',
    });

    const parts = formatter.formatToParts(newDate);
    const hour = parts.find((p) => p.type === 'hour')?.value;
    const minute = parts.find((p) => p.type === 'minute')?.value;

    return minute === '00' ? `${hour}` : `${hour}.${minute}`;
};

export const getTimeslotRange = (startHour: string, endHour: string) => {
    return `kl. ${startHour}–${endHour}`;
};

export const parseTimeslotDate = (startTime: number | Date, endTime: number | Date) => {
    const startDate = new Date(startTime);

    const dayNumber = startDate.getDate();
    const monthName = Helpers.getMonthName(startDate, true).toLowerCase();
    const weekdayShortName = Helpers.getDayName(startDate.getDay(), true).toLowerCase();
    const weekdayName = Helpers.getDayName(startDate.getDay()).toLowerCase();
    const isToday = Helpers.dateIsToday(startDate);
    const isTomorrow = Helpers.dateIsTomorrow(startDate);

    const getWeekDayFormatted = (short?: boolean) => {
        if (short) {
            return `${weekdayShortName} ${dayNumber} ${monthName}`;
        }

        return `${weekdayName} ${dayNumber} ${monthName}`;
    };

    const getTimeslotDay = (short: boolean) => {
        if (isToday) {
            return 'idag';
        }

        if (isTomorrow) {
            return 'imorgon';
        }

        return getWeekDayFormatted(short);
    };

    const timeslotDay = getTimeslotDay(true);

    const startHour = formatHour(startTime);
    const endHour = formatHour(endTime);

    return {
        isToday,
        isTomorrow,
        weekdayName,
        weekdayShortName,
        dayNumber,
        timeslotDay,
        getTimeslotDay,
        getWeekDayFormatted,
        monthName,
        startHour,
        endHour,
        timeRange: getTimeslotRange(startHour, endHour),
    };
};

/**
 * Checks if the time passes in X miliseconds
 */
export const willTimePassIn = (time: Date, ms: number) => {
    const now = new Date();

    if (isAfter(now, time)) {
        return false; // the given time is in the past
    }

    now.setMilliseconds(now.getMilliseconds() + ms);

    const timeslotWillSoonPass = isAfter(now, time);

    return timeslotWillSoonPass;
};
